/**
 * Tiny in-process pub/sub that carries a freshly-arrived notification from the
 * realtime SSE reader (a pure data hook) to the UI layer that shows the live
 * toast (`useLiveNotificationToasts`, mounted at the app root with router
 * access).
 *
 * Kept deliberately outside React/zustand: the SSE reader isn't a component,
 * and this avoids a re-render on every event — only the toast subscriber runs.
 */

export interface LiveNotification {
  /** Inbox notification id; null when the inbox transport was off. */
  id: string | null;
  kind: string;
  title: string;
  body: string;
  /** In-app destination (currently the inbox; per-item deep-links are a
   *  fast-follow). */
  url: string;
  /** High-signal kinds only should interrupt with a foreground toast. */
  important: boolean;
}

type Listener = (n: LiveNotification) => void;

const listeners = new Set<Listener>();

export function emitLiveNotification(n: LiveNotification): void {
  for (const listener of listeners) {
    try {
      listener(n);
    } catch {
      /* a broken subscriber must not break the SSE reader */
    }
  }
}

export function onLiveNotification(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
